import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const Terms = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <Link to="/" className="back-link">← Back to Home</Link>
          <h1>Terms of Service</h1>
          <p className="last-updated">Last Updated: March 4, 2026</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using Genia+ ("Service"), you accept and agree to be bound by the terms 
              and provision of this agreement. If you do not agree to these Terms of Service, please do 
              not use the Service.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              Genia+ provides AI-powered tools and analytics for Amazon FBA sellers, including but not 
              limited to:
            </p>
            <ul>
              <li>Product and order synchronization with Amazon SP-API</li>
              <li>Profit tracking and analytics</li>
              <li>AI-powered chat assistant</li>
              <li>FBA fee calculator</li>
              <li>Predictive alerts</li>
              <li>Money recovery assistance</li>
              <li>Amazon Ads management (Pro plan)</li>
            </ul>
          </section>

          <section>
            <h2>3. User Accounts</h2>
            <h3>3.1 Registration</h3>
            <p>
              To use the Service, you must register for an account. You agree to provide accurate, 
              current, and complete information during registration and to update such information to 
              keep it accurate, current, and complete.
            </p>
            
            <h3>3.2 Account Security</h3>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and 
              for all activities that occur under your account. You agree to notify us immediately of 
              any unauthorized use of your account.
            </p>

            <h3>3.3 Account Termination</h3>
            <p>
              We reserve the right to suspend or terminate your account at any time for violation of 
              these Terms of Service or for any other reason at our sole discretion.
            </p>
          </section>

          <section>
            <h2>4. Subscription and Payment</h2>
            <h3>4.1 Free Trial</h3>
            <p>
              We offer a 14-day free trial for new users. No credit card is required to start the trial. 
              After the trial period, you must subscribe to a paid plan to continue using the Service.
            </p>

            <h3>4.2 Subscription Plans</h3>
            <p>
              We offer two subscription plans: Starter ($49/month) and Pro ($149/month). Plan features 
              and pricing are subject to change with 30 days notice.
            </p>

            <h3>4.3 Payment</h3>
            <p>
              Subscriptions are billed monthly in advance. Payment is processed through Stripe. By 
              subscribing, you authorize us to charge your payment method on a recurring basis.
            </p>

            <h3>4.4 Refunds</h3>
            <p>
              We offer a 30-day money-back guarantee. If you're not satisfied with the Service within 
              the first 30 days of your paid subscription, contact us for a full refund.
            </p>

            <h3>4.5 Cancellation</h3>
            <p>
              You may cancel your subscription at any time. Cancellation will take effect at the end of 
              your current billing period. No refunds will be provided for partial months.
            </p>
          </section>

          <section>
            <h2>5. Amazon API Integration</h2>
            <h3>5.1 Amazon Credentials</h3>
            <p>
              To use the Service, you must provide your Amazon SP-API credentials. You are responsible 
              for obtaining and maintaining valid credentials from Amazon.
            </p>

            <h3>5.2 Data Access</h3>
            <p>
              By providing your Amazon credentials, you authorize us to access your Amazon seller data 
              on your behalf. We will only access data necessary to provide the Service.
            </p>

            <h3>5.3 Amazon Terms</h3>
            <p>
              You agree to comply with all Amazon Seller Central terms and conditions. We are not 
              responsible for any violations of Amazon's terms that result from your use of the Service.
            </p>
          </section>

          <section>
            <h2>6. Data and Privacy</h2>
            <h3>6.1 Data Collection</h3>
            <p>
              We collect and process your data as described in our Privacy Policy. By using the Service, 
              you consent to such collection and processing.
            </p>

            <h3>6.2 Data Security</h3>
            <p>
              We implement industry-standard security measures to protect your data, including encryption 
              of sensitive information. However, no method of transmission over the Internet is 100% secure.
            </p>

            <h3>6.3 Data Ownership</h3>
            <p>
              You retain all rights to your data. We do not claim ownership of any data you provide to 
              the Service.
            </p>
          </section>

          <section>
            <h2>7. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any illegal purpose</li>
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Transmit viruses or malicious code</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use the Service to spam or harass others</li>
              <li>Resell or redistribute the Service</li>
            </ul>
          </section>

          <section>
            <h2>8. Intellectual Property</h2>
            <h3>8.1 Our Rights</h3>
            <p>
              The Service and its original content, features, and functionality are owned by Genia+ and 
              are protected by international copyright, trademark, and other intellectual property laws.
            </p>

            <h3>8.2 License</h3>
            <p>
              We grant you a limited, non-exclusive, non-transferable license to access and use the 
              Service for your personal or business use, subject to these Terms.
            </p>
          </section>

          <section>
            <h2>9. Disclaimers</h2>
            <h3>9.1 Service Availability</h3>
            <p>
              The Service is provided "as is" and "as available" without warranties of any kind. We do 
              not guarantee that the Service will be uninterrupted, secure, or error-free.
            </p>

            <h3>9.2 Accuracy</h3>
            <p>
              While we strive for accuracy, we do not warrant that the information provided by the 
              Service is accurate, complete, or current. You should verify all information independently.
            </p>

            <h3>9.3 Third-Party Services</h3>
            <p>
              The Service integrates with third-party services (Amazon, Stripe, etc.). We are not 
              responsible for the availability or accuracy of these third-party services.
            </p>
          </section>

          <section>
            <h2>10. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Genia+ shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages, or any loss of profits or revenues, 
              whether incurred directly or indirectly, or any loss of data, use, goodwill, or other 
              intangible losses resulting from:
            </p>
            <ul>
              <li>Your use or inability to use the Service</li>
              <li>Any unauthorized access to or use of our servers</li>
              <li>Any interruption or cessation of the Service</li>
              <li>Any bugs, viruses, or other harmful code</li>
              <li>Any errors or omissions in any content</li>
            </ul>
          </section>

          <section>
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Genia+ and its officers, directors, employees, 
              and agents from any claims, damages, losses, liabilities, and expenses (including legal 
              fees) arising from your use of the Service or violation of these Terms.
            </p>
          </section>

          <section>
            <h2>12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of any material 
              changes by email or through the Service. Your continued use of the Service after such 
              changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2>13. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United 
              States, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2>14. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p>
              Email: support@geniaplus.com<br />
              Address: [Your Business Address]
            </p>
          </section>
        </div>

        <div className="legal-footer">
          <p>
            By using Genia+, you acknowledge that you have read, understood, and agree to be bound by 
            these Terms of Service.
          </p>
          <div className="legal-links">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/">Back to Home</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
